/**
 * BkPauseSetting — Admin-Einstellungen → BK Stages.
 *
 * Pause einer Betreuungskraft (Tage) nach einer Betreuung, wenn an der
 * Betreuung „Auto-Pause" (autobreak) aktiv ist. Default 21, Bereich 0..365
 * (0 = keine Pause). Der CareStateWorker setzt daraus den Status
 * „Pausierend" und das „Verfügbar ab"-Datum (users.startdatum), nach dem
 * auch das Matching sortiert.
 *
 * Liest/schreibt gezielt nur diesen einen Key über `bkPauseDaysApi`
 * (Muster wie WiedervorlageMaxSetting).
 */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, SectionTitle, Button, Field, Input } from '../../components/ui';
import { getThemeColors } from '../../lib/theme';
import { bkPauseDaysApi, BK_PAUSE_DAYS_DEFAULT } from '../../lib/bkPauseSetting';

interface BkPauseSettingProps {
  dark: boolean;
}

export function BkPauseSetting({ dark }: BkPauseSettingProps) {
  const t = getThemeColors(dark);
  const { t: tr } = useTranslation();

  const [value, setValue] = useState<string>(String(BK_PAUSE_DAYS_DEFAULT));
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const days = await bkPauseDaysApi.get();
        if (!cancelled) setValue(String(days));
      } catch {
        if (!cancelled) setLoadError(tr('settings.bk_pause_days.load_error'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [tr]);

  const save = async () => {
    const trimmed = value.trim();
    const parsed = Number(trimmed);
    if (trimmed === '' || !Number.isInteger(parsed) || parsed < 0 || parsed > 365) {
      setSaveError(tr('settings.bk_pause_days.error_range'));
      return;
    }
    setSaving(true);
    setSaveError(null);
    setSaved(false);
    try {
      const updated = await bkPauseDaysApi.update(parsed);
      setValue(String(updated));
      setSaved(true);
    } catch {
      setSaveError(tr('settings.bk_pause_days.error_save'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card dark={dark} style={{ padding: 20, maxWidth: 420 }}>
      <SectionTitle
        dark={dark}
        title={tr('settings.bk_pause_days.title')}
        subtitle={tr('settings.bk_pause_days.subtitle')}
      />
      {loading ? (
        <div style={{ fontSize: 13, color: t.muted, padding: '8px 0' }}>{tr('common.loading')}</div>
      ) : (
        <>
          {loadError && <div style={{ fontSize: 12, color: '#b42318', marginBottom: 8 }}>{loadError}</div>}
          <Field label={tr('settings.bk_pause_days.field_label')}>
            <Input
              dark={dark}
              type="number"
              min={0}
              max={365}
              value={value}
              onChange={e => { setValue(e.target.value); setSaved(false); setSaveError(null); }}
              aria-label={tr('settings.bk_pause_days.field_label')}
            />
          </Field>
          <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 10 }}>
            <Button variant="primary" size="sm" dark={dark} onClick={() => void save()} disabled={saving}>
              {saving ? tr('settings.bk_pause_days.saving') : tr('common.action.save')}
            </Button>
            {saved && <span style={{ fontSize: 12, color: '#039855', fontWeight: 600 }}>{tr('settings.bk_pause_days.saved')}</span>}
            {saveError && <span style={{ fontSize: 12, color: '#b42318', fontWeight: 600 }}>{saveError}</span>}
          </div>
        </>
      )}
    </Card>
  );
}
